<template>
  <div v-click-outside="close_calendar" class="date-range-picker">
    <!-- Single combined input -->
    <div class="date-range-picker__input-wrapper">
      <div
        class="date-range-picker__input cursor-pointer"
        @click="toggle_calendar"
      >
        <div class="date-range-picker__icon">
          <vb-icon :name="'calendar'" :color="sass('primary')" :size="20" />
        </div>
        <b-form-input
          :value="display_text"
          placeholder="Quando?"
          class="vb-input"
        />
      </div>

      <!-- Single Calendar (range or single date mode) -->
      <div v-show="calendar_open" class="vc-wrapper">
        <v-date-picker
          v-if="range"
          :ref="`range:${calendar_ref}`"
          v-model="calendar_range"
          is-range
          :model-config="model_config"
          :min-date="min_date"
          :max-date="max_date"
          :select-attribute="select_attrs"
          :drag-attribute="drag_attrs"
          @input="on_range_selected"
          />
          <v-date-picker
          v-else
          :ref="calendar_ref"
          v-model="calendar_single"
          :model-config="model_config"
          :min-date="min_date"
          :max-date="max_date"
          :select-attribute="select_attrs"
          :drag-attribute="drag_attrs"
          @input="on_single_selected"
        />
        <div v-if="disclaimer" class="picker-alert">
          <b-alert show variant="warning" size="xs" v-html="disclaimer" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Vue from 'vue';
  import VCalendar from 'v-calendar';
  import { move_picker, select_attrs, drag_attrs } from '@/utilities/calendar';
  import {
    formatDateDisplay,
    toDate,
    toDateString,
  } from '@liveflight/utilities/dates';

  Vue.use(VCalendar);

  export default {
    name: 'date-range-picker',
    props: {
      // Dates array: [0] = departure, [1] = return, [2+] = multi-itinerary legs
      dates: {
        type: Array,
        required: true,
        default: () => [],
      },
      // Whether to use range selection mode
      range: {
        type: Boolean,
        default: false,
      },
      min_date: {
        type: Date,
        required: true,
      },
      max_date: {
        type: Date,
        required: true,
      },
      disclaimer: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        model_config: {
          type: 'string',
          mask: 'YYYY-MM-DD',
        },
        calendar_ref: `calendar:${this._uid}`,
        calendar_open: false,
        select_attrs,
        drag_attrs,
      };
    },
    computed: {
      departure_date() {
        return this.dates[0] || '';
      },
      return_date() {
        return this.dates[1] || '';
      },
      display_text() {
        if (!this.departure_date) return '';

        const departure = formatDateDisplay(this.departure_date);

        if (!this.range) {
          return departure;
        }

        if (!this.return_date) {
          return departure;
        }

        const returnDate = formatDateDisplay(this.return_date);
        return `${departure} - ${returnDate}`;
      },
      // For range mode: v-calendar expects { start, end } object
      calendar_range: {
        get() {
          if (!this.departure_date) return null;
          return {
            start: toDate(this.departure_date),
            end: this.return_date
              ? toDate(this.return_date)
              : toDate(this.departure_date),
          };
        },
        // eslint-disable-next-line no-unused-vars, no-empty-function
        set(value) {},
      },
      // For single date mode
      calendar_single: {
        get() {
          return this.departure_date;
        },
        // eslint-disable-next-line no-unused-vars, no-empty-function
        set(value) {},
      },
    },
    methods: {
      toggle_calendar() {
        this.calendar_open = !this.calendar_open;
        if (!this.calendar_open || !this.departure_date) {
          return;
        }

        const key = this.range
          ? `range:${this.calendar_ref}`
          : this.calendar_ref;
        const picker = this.$refs[key];
        if (picker) {
          move_picker(picker, this.departure_date);
        }
      },
      close_calendar() {
        this.calendar_open = false;
      },
      on_range_selected(value) {
        if (!value || !value.start) return;

        const start = toDateString(value.start);
        const end = value.end ? toDateString(value.end) : start;

        // Only close when both dates are selected
        if (value.start && value.end) {
          const new_dates = [...this.dates];
          new_dates[0] = start;
          new_dates[1] = end;
          this.$emit('update:dates', new_dates);
          this.close_calendar();
        }
      },
      on_single_selected(value) {
        if (!value) return;

        const new_dates = [...this.dates];
        new_dates[0] = value;
        this.$emit('update:dates', new_dates);
        this.close_calendar();
      },
    },
  };
</script>

<style lang="scss" scoped>
  .date-range-picker {
    &__input-wrapper {
      position: relative;
    }

    &__input {
      position: relative;
      input {
        padding-left: 40px;
        border: 1px solid var(--primary);
        pointer-events: none;
        &::placeholder {
          font-size: $font-size-base;
          color: var(--text);
        }
      }
    }

    &__icon {
      position: absolute;
      top: 50%;
      left: 10px;
      height: 26px;
      transform: translateY(-50%);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1;
    }

    ::v-deep .vc-wrapper {
      position: absolute;
      top: 100%;
      left: 0;
      z-index: 1050;
      margin-top: 5px;
    }

    ::v-deep .vc-container {
      background-color: $white;
      border: 1px solid $gray-200;
      box-shadow: $box-shadow-sm;
      border-radius: $btn-border-radius;
      width: 100%;
    }

    ::v-deep .picker-alert {
      margin-top: -5px;
      .alert {
        font-size: $font-size-sm;
        margin: 0;
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      }
    }

    @include media-breakpoint-down(sm) {
      ::v-deep .vc-wrapper {
        position: static;
        margin-top: 0;
      }
    }
  }
</style>
